import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowRight, ChevronLeft, ChevronRight, Shield, Target, Eye, AlertTriangle } from 'lucide-react';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_358eaf41-2a2d-4776-981f-b103a2635861/artifacts/wk2mqae4_Landing%20Page%20Photo.png';
const HERO_VIDEO = 'https://customer-assets.emergentagent.com/job_358eaf41-2a2d-4776-981f-b103a2635861/artifacts/alisfw7c_Landing%20Page%20Video.mp4';

const HUD_INSTRUMENTS = [
  { label: 'Trust', icon: Shield },
  { label: 'Efficacy', icon: Target },
  { label: 'Clarity', icon: Eye },
  { label: 'Risk', icon: AlertTriangle },
];

const SCREENS = [
  {
    id: 'launch',
    label: 'Decision Rehearsal',
    headline: 'This is not a course.',
    subhead: 'This is a decision rehearsal for school leaders.',
    body: 'Most professional development asks you to absorb ideas. This experience asks you to use them. Your choices will shape **trust**, **clarity**, and **what becomes possible next**.',
    cta: 'Start Rehearsal',
  },
  {
    id: 'journey',
    label: 'Your Journey',
    headline: 'How you will grow.',
    steps: [
      { num: '01', title: 'Learn the conversation moves', desc: 'Master the framework that separates reactive managers from intentional leaders.' },
      { num: '02', title: 'Build Your Intent', desc: 'Before you speak, know what you\'re trying to accomplish.' },
      { num: '03', title: 'Rehearse the Room', desc: 'Practice the conversation in a high-fidelity simulation.' },
      { num: '04', title: 'Review your impact', desc: 'See exactly where your words landed — and where they missed.' },
    ],
    cta: 'See the Agenda',
  },
  {
    id: 'context',
    label: 'Mission Context',
    headline: 'October.',
    subhead: 'The year is no longer new.',
    body: 'It\'s mid-fall. Initial goodwill is fading, routines are under pressure, and concerns are starting to surface. A parent has raised a concern about Ms. Ortega, a respected veteran teacher. She knows leadership has seen the complaint. The facts are still incomplete.',
    timeline: ['AUG', 'SEP', 'OCT', 'NOV', 'DEC', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN'],
    activeMonth: 2,
    cta: 'Begin Briefing',
  },
];

function renderBodyWhite(text) {
  if (!text) return null;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={i} className="text-[#00A9E0] font-semibold">{part}</strong>
      : <span key={i}>{part}</span>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const glassSlide = {
  hidden: { opacity: 0, x: 20, backdropFilter: 'blur(0px)' },
  visible: {
    opacity: 1,
    x: 0,
    backdropFilter: 'blur(24px)',
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1], delay: 0.15 },
  },
};

export function Onboarding({ onComplete }) {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [bgReady, setBgReady] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);
  const screen = SCREENS[currentScreen];
  const isLaunch = currentScreen === 0;

  // Preload the background image once on mount
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBgReady(true);
    img.src = HERO_IMAGE;
  }, []);

  // Play video when ready
  const handleVideoCanPlay = useCallback(() => {
    setVideoReady(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const handleNext = () => {
    if (currentScreen < SCREENS.length - 1) {
      setCurrentScreen(currentScreen + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentScreen > 0) {
      setCurrentScreen(currentScreen - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#002B45]" data-testid="onboarding">
      {/* ── Persistent background layer (never unmounts) ── */}
      <div className="absolute inset-0">
        {/* Static image — always present as base layer */}
        <img
          src={HERO_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: bgReady ? 1 : 0 }}
        />

        {/* Video — overlays image on launch screen, fades in when ready */}
        <video
          ref={videoRef}
          src={HERO_VIDEO}
          poster={HERO_IMAGE}
          muted
          loop
          playsInline
          onCanPlay={handleVideoCanPlay}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: isLaunch && videoReady ? 1 : 0 }}
          data-testid="hero-video"
        />

        {/* Dark-blue color grade overlay */}
        <div className="absolute inset-0 bg-[#002B45]/75 mix-blend-multiply" />
        {/* Radial light source from center-right */}
        <div className="absolute inset-0 bg-radial-light" />
      </div>

      {/* ── Animated content layer ── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={screen.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.3 } }}
          transition={{ duration: 0.5 }}
          className="h-full w-full relative z-10"
        >
          {/* Logo — top left */}
          <motion.div
            custom={0} variants={fadeUp} initial="hidden" animate="visible"
            className="absolute top-6 left-6 sm:top-8 sm:left-8 z-20"
          >
            <span className="text-white/40 text-[10px] font-medium uppercase tracking-[0.3em]">
              Leader Cadence
            </span>
          </motion.div>

          {/* Skip — top right */}
          <button
            onClick={onComplete}
            className="absolute top-6 right-6 sm:top-8 sm:right-8 text-white/20 text-[10px] uppercase tracking-widest hover:text-white/50 transition-colors z-20"
            data-testid="onboarding-skip"
          >
            Skip
          </button>

          {/* ──── Main Layout: 60/40 Split ──── */}
          <div className="relative h-full flex flex-col lg:flex-row">

            {/* Left Pane — Cinematic (60%) */}
            <div className="flex-1 lg:w-[60%] relative flex items-end p-6 sm:p-10 lg:p-14">
              {/* Large screen number watermark (screens 2-3 only) */}
              {!isLaunch && (
                <motion.span
                  custom={0} variants={fadeUp} initial="hidden" animate="visible"
                  className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 text-white/[0.04] text-[140px] lg:text-[200px] font-headline font-bold leading-none select-none pointer-events-none"
                >
                  0{currentScreen + 1}
                </motion.span>
              )}
            </div>

            {/* Right Pane — Glass Data Panel (40%) */}
            <motion.div
              variants={glassSlide}
              initial="hidden"
              animate="visible"
              className="w-full lg:w-[40%] h-[65vh] lg:h-full flex-shrink-0 relative"
            >
              <div className="absolute inset-0 lg:inset-y-0 lg:left-0 lg:right-0 glass-cockpit" />

              <div className="relative h-full flex flex-col justify-between p-5 sm:p-10 lg:p-12 xl:p-14 overflow-y-auto custom-scrollbar">

                {/* Top: System Status Bar + HUD */}
                <div>
                  {/* System init bar */}
                  <div className="mb-5">
                    <div className="h-[2px] w-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full bg-[#78BE20] status-bar-pulse" />
                    </div>
                  </div>

                  {/* HUD Instrument Icons */}
                  <motion.div
                    custom={1} variants={fadeUp} initial="hidden" animate="visible"
                    className="flex items-center gap-5 mb-2"
                    data-testid="hud-preview"
                  >
                    {HUD_INSTRUMENTS.map((inst) => {
                      const Icon = inst.icon;
                      return (
                        <div key={inst.label} className="flex items-center gap-1.5 opacity-40">
                          <Icon className="w-3 h-3 text-[#00A9E0]" strokeWidth={1.5} />
                          <span className="text-[9px] uppercase tracking-wider text-white/60 font-medium">
                            {inst.label}
                          </span>
                        </div>
                      );
                    })}
                  </motion.div>

                  <div className="divider-glow my-5" />
                </div>

                {/* Center: Content */}
                <div className="flex-1 flex flex-col justify-center max-w-md">
                  {/* Label + active dot */}
                  <motion.div
                    custom={2} variants={fadeUp} initial="hidden" animate="visible"
                    className="flex items-center gap-2 mb-5"
                  >
                    <span className="dot-active" />
                    <span className="text-[#00A9E0] text-[11px] font-semibold uppercase tracking-[0.25em]">
                      {screen.label}
                    </span>
                  </motion.div>

                  {/* Headline */}
                  <motion.h1
                    custom={3} variants={fadeUp} initial="hidden" animate="visible"
                    className="text-white text-3xl sm:text-4xl lg:text-[44px] font-headline font-extrabold leading-[1.08] tracking-tight mb-4 text-glow"
                    data-testid="launch-headline"
                  >
                    {screen.headline}
                  </motion.h1>

                  {/* Subhead */}
                  {screen.subhead && (
                    <motion.p
                      custom={4} variants={fadeUp} initial="hidden" animate="visible"
                      className="text-white/80 text-base sm:text-lg lg:text-xl font-headline font-medium leading-relaxed mb-6"
                    >
                      {screen.subhead}
                    </motion.p>
                  )}

                  {/* Body */}
                  {screen.body && (
                    <motion.p
                      custom={5} variants={fadeUp} initial="hidden" animate="visible"
                      className="text-white/40 text-sm sm:text-[15px] leading-[1.8] mb-8"
                    >
                      {renderBodyWhite(screen.body)}
                    </motion.p>
                  )}

                  {/* Steps (Screen 2) */}
                  {screen.steps && (
                    <div className="space-y-4 mb-8">
                      {screen.steps.map((step, i) => (
                        <motion.div
                          key={step.num}
                          custom={i + 3}
                          variants={fadeUp}
                          initial="hidden"
                          animate="visible"
                          className="flex items-start gap-4 group"
                        >
                          <span className="text-[#00A9E0]/40 text-xs font-mono font-bold mt-0.5 w-5 flex-shrink-0 group-hover:text-[#00A9E0] transition-colors">
                            {step.num}
                          </span>
                          <div>
                            <h3 className="text-white text-sm font-headline font-semibold mb-0.5">
                              {step.title}
                            </h3>
                            <p className="text-white/30 text-xs leading-relaxed">
                              {step.desc}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Timeline (Screen 3) */}
                  {screen.timeline && (
                    <motion.div
                      custom={5} variants={fadeUp} initial="hidden" animate="visible"
                      className="mb-8"
                    >
                      <div className="flex items-center gap-0">
                        {screen.timeline.map((month, i) => (
                          <div key={month} className="flex-1 flex flex-col items-center">
                            <div
                              className={`w-full h-[3px] ${
                                i === screen.activeMonth ? 'bg-[#FFB81C] glow-green-sm' :
                                i < screen.activeMonth ? 'bg-[#78BE20]' : 'bg-white/10'
                              }`}
                            />
                            <span className={`text-[7px] sm:text-[9px] mt-1.5 ${
                              i === screen.activeMonth ? 'text-[#FFB81C] font-bold' :
                              i < screen.activeMonth ? 'text-[#78BE20]' : 'text-white/20'
                            }`}>
                              {month}
                            </span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* CTA */}
                  <motion.div custom={7} variants={fadeUp} initial="hidden" animate="visible">
                    <Button
                      onClick={handleNext}
                      className="btn-instrument text-white text-sm uppercase tracking-widest px-8 py-6 rounded"
                      data-testid="onboarding-next"
                    >
                      {screen.cta}
                      <ArrowRight className="w-4 h-4 ml-3" />
                    </Button>
                  </motion.div>
                </div>

                {/* Bottom: Progress with subtle < > */}
                <motion.div
                  custom={8} variants={fadeUp} initial="hidden" animate="visible"
                  className="flex items-center justify-center gap-3 pt-4"
                >
                  {/* Back chevron */}
                  <button
                    onClick={handleBack}
                    disabled={currentScreen === 0}
                    className={`p-1 transition-opacity ${
                      currentScreen === 0 ? 'opacity-0 pointer-events-none' : 'opacity-20 hover:opacity-50'
                    }`}
                    data-testid="onboarding-back"
                    aria-label="Previous"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                  </button>

                  {/* Progress dots */}
                  <div className="flex items-center gap-2.5">
                    {SCREENS.map((_, i) => (
                      <div
                        key={i}
                        className={`h-[2px] transition-all duration-500 ${
                          i === currentScreen
                            ? 'w-10 bg-[#00A9E0] glow-blue-sm'
                            : i < currentScreen
                              ? 'w-5 bg-[#78BE20]'
                              : 'w-5 bg-white/10'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Next chevron */}
                  <button
                    onClick={handleNext}
                    className="p-1 opacity-20 hover:opacity-50 transition-opacity"
                    data-testid="onboarding-forward"
                    aria-label="Next"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default Onboarding;
